import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Download, FileText, TrendingUp, Users, Award, Trophy, RefreshCw } from 'lucide-react'
import { apiGet } from '../lib/api'
import type { ComplianceData } from '../../../shared/types'

interface PowerUser {
  id: string
  name: string
  email: string
  count: number
}

interface ExtendedComplianceData extends ComplianceData {
  powerUsers?: PowerUser[]
  topRenewalTracks?: { name: string; count: number }[]
}

export default function ReportsPage() {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'compliance'],
    queryFn: () => apiGet<{ compliance: ExtendedComplianceData }>('/admin/compliance'),
  })

  const compliance = data?.compliance

  const handleExportCSV = () => {
    if (!compliance) return
    
    const rows = [
      ['Metric', 'Value'],
      ['Total Users', compliance.totalAllUsers],
      ['Active Users', compliance.activeUsersWithCerts],
      ['Total Certifications', compliance.totalCertifications],
      ['Avg Certs per User', compliance.avgCertsPerActiveUser],
      ['Users (role)', compliance.usersByRole.user],
      ['Admins (role)', compliance.usersByRole.admin],
      ['Super Admins (role)', compliance.usersByRole.super_admin],
      ['New Users Today', compliance.newUsers.today],
      ['New Users This Week', compliance.newUsers.thisWeek],
      ['New Users This Month', compliance.newUsers.thisMonth],
      ['New Certs Today', compliance.newCertifications.today],
      ['New Certs This Week', compliance.newCertifications.thisWeek],
      ['New Certs This Month', compliance.newCertifications.thisMonth],
    ]
    
    const csvContent = rows.map(row => row.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `compliance-report-${new Date().toISOString().split('T')[0]}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="text-gray-500 mt-1">Compliance analytics and data export</p>
        </div>
        <button onClick={handleExportCSV} className="inline-flex items-center justify-center px-4 py-2 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-50 rounded-xl">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{compliance?.totalAllUsers || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-50 rounded-xl">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Users</p>
              <p className="text-2xl font-bold text-gray-900">{compliance?.activeUsersWithCerts || 0}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-50 rounded-xl">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Certifications</p>
              <p className="text-2xl font-bold text-gray-900">{compliance?.totalCertifications || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Reports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growth Report */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-400" />
            Growth Report
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-gray-500 font-medium">Period</th>
                  <th className="text-right py-2 text-gray-500 font-medium">New Users</th>
                  <th className="text-right py-2 text-gray-500 font-medium">New Certs</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="py-3">Today</td>
                  <td className="text-right font-medium">{compliance?.newUsers.today || 0}</td>
                  <td className="text-right font-medium">{compliance?.newCertifications.today || 0}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">This Week</td>
                  <td className="text-right font-medium">{compliance?.newUsers.thisWeek || 0}</td>
                  <td className="text-right font-medium">{compliance?.newCertifications.thisWeek || 0}</td>
                </tr>
                <tr className="border-b">
                  <td className="py-3">This Month</td>
                  <td className="text-right font-medium">{compliance?.newUsers.thisMonth || 0}</td>
                  <td className="text-right font-medium">{compliance?.newCertifications.thisMonth || 0}</td>
                </tr>
                <tr>
                  <td className="py-3">This Year</td>
                  <td className="text-right font-medium">{compliance?.newUsers.thisYear || 0}</td>
                  <td className="text-right font-medium">{compliance?.newCertifications.thisYear || 0}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Certifications */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-gray-400" />
            Top Certifications
          </h2>
          <div className="space-y-3">
            {compliance?.topCertifications?.slice(0, 8).map((cert, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{cert.name}</span>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">{cert.count}</span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">No certification data available</p>
            )}
          </div>
        </div>

        {/* Top Professions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-gray-400" />
            Top Professions
          </h2>
          <div className="space-y-3">
            {compliance?.topProfessions?.slice(0, 8).map((prof, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{prof.name}</span>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">{prof.count} users</span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">No profession data available</p>
            )}
          </div>
        </div>

        {/* Top Renewal Tracks */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-gray-400" />
            Top Renewal Tracks
          </h2>
          <div className="space-y-3">
            {compliance?.topRenewalTracks?.slice(0, 8).map((track, index) => (
              <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 bg-gray-100 rounded-full flex items-center justify-center text-xs font-medium text-gray-600">
                    {index + 1}
                  </span>
                  <span className="text-gray-700">{track.name}</span>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600">{track.count} users</span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">No renewal track data available</p>
            )}
          </div>
        </div>

        {/* Top Users */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-500" />
            Top Users
          </h2>
          <div className="space-y-3">
            {compliance?.powerUsers?.slice(0, 5).map((user, index) => (
              <div 
                key={user.id} 
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded"
                onClick={() => navigate(`/admin/users/${user.id}`)}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    index === 0 ? 'bg-amber-100 text-amber-700' :
                    index === 1 ? 'bg-gray-200 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div>
                    <span className="text-gray-900 font-medium">{user.name}</span>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </div>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                  {user.count} certs
                </span>
              </div>
            )) || (
              <p className="text-gray-500 text-center py-4">No user data available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
